import { Component, effect, inject, input, model, OnInit, output, signal } from '@angular/core'

import { TreeNode } from 'primeng/api'
import { SkeletonModule } from 'primeng/skeleton'
import { TreeModule } from 'primeng/tree'

import { Category } from 'app/_shared/interfaces/Category'
import { ProductService } from 'app/_shared/services/product.service'
import { AppConfigService } from 'app/app-config.service'

@Component({
  selector: 'app-category-filter',
  imports: [TreeModule, SkeletonModule],
  templateUrl: './category-filter.component.html',
  styleUrl: './category-filter.component.scss'
})
export class CategoryFilterComponent implements OnInit {
  private appConfigService = inject(AppConfigService)
  private productService = inject(ProductService)

  filtersChanged = output()
  leadCategoryId = input<number>()
  selectedCategoryIds = model.required<number[]>()
  hasCategories = model<boolean>()

  categories = signal<Category[]>([])

  private storefrontId!: number
  tree: TreeNode[] = []
  selection: TreeNode[] = []
  loading = true

  selectedChange = effect(() => {
    const value = this.selectedCategoryIds()
    if (value.length && this.tree) {
      this.selection = this.preselectCategories(this.tree, this.selectedCategoryIds())
    } else {
      this.selection = []
    }
  })

  ngOnInit(): void {
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.fetchCategories()
  }

  private fetchCategories(): void {
    this.loading = true
    const companyCategoryId = this.leadCategoryId() ? this.leadCategoryId() : undefined
    this.productService.getProductCategories(this.storefrontId, companyCategoryId).subscribe({
      next: res => {
        this.categories.set(res.data)
        if (!res.data.length) {
          this.hasCategories.set(false)
        }
        this.tree = this.mapToTree(this.categories())
        if (this.selectedCategoryIds().length) {
          this.selection = this.preselectCategories(this.tree, this.selectedCategoryIds())
        }
        this.loading = false
      },
      error: () => (this.loading = false)
    })
  }

  private mapToTree(categories: Category[], parentKey: string = '0'): TreeNode[] {
    return categories.map((category, index) => {
      const currentKey = `${parentKey}-${index}`
      const hasSubCategories = category.sub_categories && category.sub_categories.length > 0
      const transformed: TreeNode = {
        key: currentKey,
        label: category.name,
        data: category.id
      }

      if (hasSubCategories) {
        transformed.children = this.mapToTree(category.sub_categories, currentKey)
      }

      return transformed
    })
  }

  preselectCategories(tree: any[], ids: number[]): any[] {
    const selectedNodes: any[] = []

    const traverse = (nodes: any[]): void => {
      for (const node of nodes) {
        if (ids.includes(node.data)) {
          selectedNodes.push(node) // Add node to selected
        }
        if (node.children) {
          traverse(node.children) // Recursively check children
        }
      }
    }

    traverse(tree)
    return selectedNodes
  }

  handleSelectionChange(): void {
    this.selectedCategoryIds.set(this.selection.map(selection => selection.data))
    this.filtersChanged.emit()
  }
}
